// 军师增长平台 — markdown 子集解析器(纯函数,零依赖,无 React/DOM)。
// 覆盖军师 DeepSeek 实测语法面:标题 / GFM 表格 / 列表 / 加粗 / 行内代码 / 段落。
// 未覆盖语法(链接/嵌套列表/italic/围栏代码块)优雅降级为纯文本,永不比纯文本现状差。

export type TextSpan =
  | { kind: "text"; value: string }
  | { kind: "bold"; value: string }
  | { kind: "code"; value: string };

// 行内解析:先 code(`...`)后 bold(**...**),避免 code 内的 ** 被误吃。
// 未闭合标记落回字面文本。
export function parseInline(text: string): TextSpan[] {
  const spans: TextSpan[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) spans.push({ kind: "text", value: buf });
    buf = "";
  };
  while (i < text.length) {
    if (text[i] === "`") {
      const end = text.indexOf("`", i + 1);
      if (end > i) {
        flush();
        spans.push({ kind: "code", value: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (text[i] === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end > i + 1) {
        flush();
        spans.push({ kind: "bold", value: text.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    buf += text[i];
    i += 1;
  }
  flush();
  return spans;
}

export type MdBlock =
  | { type: "heading"; level: number; spans: TextSpan[] }
  | { type: "table"; headers: TextSpan[][]; rows: TextSpan[][][] }
  | { type: "list"; ordered: boolean; items: TextSpan[][] }
  | { type: "hr" }
  | { type: "paragraph"; lines: TextSpan[][] };

// 章节分隔线:独立一行 3+ 个 -、* 或 _(GFM thematic break)。
// 表格分隔行(| --- |)由 table 分支先消费,不会落到这里。
const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function isTableSeparator(line: string): boolean {
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c.trim()));
}

const LIST_RE = /^\s*([-*+]|\d+\.)\s+(.*)$/;

export function parseMarkdown(text: string): MdBlock[] {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: MdBlock[] = [];
  let para: TextSpan[][] = [];
  const flushPara = () => {
    if (para.length) blocks.push({ type: "paragraph", lines: para });
    para = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      flushPara();
      i += 1;
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      blocks.push({ type: "heading", level: h[1].length, spans: parseInline(h[2].trim()) });
      i += 1;
      continue;
    }
    if (/^\s*\|/.test(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushPara();
      const headers = splitRow(line).map(parseInline);
      const rows: TextSpan[][][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i += 1;
      }
      blocks.push({ type: "table", headers, rows });
      continue;
    }
    if (HR_RE.test(line)) {
      flushPara();
      blocks.push({ type: "hr" });
      i += 1;
      continue;
    }
    const li = LIST_RE.exec(line);
    if (li) {
      flushPara();
      const ordered = /\d+\./.test(li[1]);
      const items: TextSpan[][] = [];
      while (i < lines.length) {
        const m = LIST_RE.exec(lines[i]);
        if (!m || /\d+\./.test(m[1]) !== ordered) break;
        items.push(parseInline(m[2].trim()));
        i += 1;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }
    para.push(parseInline(line.trim()));
    i += 1;
  }
  flushPara();
  return blocks;
}
